import { useRef, useState, type ChangeEvent } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import type { Asset } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { AttachmentPill } from "@/components/AttachmentPill";
import { AssetPreviewDialog } from "@/components/AssetPreviewDialog";
import { useAssetsByIds } from "@/hooks/useAssetsByIds";
import { uploadAsset } from "@/lib/uploadAsset";

/** Props for the AttachmentsField component. */
interface AttachmentsFieldProps {
  assetIds: string[];
  onChange: (assetIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Edit control for an attachments custom field: shows the attached assets as
 * pills with a remove control each, and uploads new files onto the list.
 */
export function AttachmentsField({
  assetIds,
  onChange,
  disabled = false,
}: AttachmentsFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<Pick<
    Asset,
    "id" | "fileName"
  > | null>(null);
  const assets = useAssetsByIds(assetIds);

  const handleFilesPicked = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) {
      return;
    }
    setIsUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map((file) => uploadAsset(file)),
      );
      onChange([...assetIds, ...uploaded.map((asset) => asset.id)]);
    } catch {
      toast.error("File upload failed. Try again.");
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {assets.map((asset) => (
        <AttachmentPill
          key={asset.id}
          fileName={asset.fileName}
          onClick={() => setPreviewAsset(asset)}
          onRemove={() => onChange(assetIds.filter((id) => id !== asset.id))}
          disabled={disabled}
        />
      ))}
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFilesPicked}
        disabled={disabled || isUploading}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-auto gap-1.5 px-2 py-1 text-xs [&_svg]:size-3"
        disabled={disabled || isUploading}
        onClick={() => inputRef.current?.click()}
      >
        <Upload />
        Upload
        {isUploading && <Loader2 className="animate-spin" />}
      </Button>
      <AssetPreviewDialog
        assetId={previewAsset?.id ?? null}
        fileName={previewAsset?.fileName}
        onClose={() => setPreviewAsset(null)}
      />
    </div>
  );
}
